<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, provide } from 'vue';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, X, Copy, Trash2, Braces } from 'lucide-vue-next';
import Button from '@/components/ui/UIButton.vue';
import Input from '@/components/ui/UIInput.vue';
import UISelect from '@/components/ui/UISelect.vue';
import Toggle from '@/components/v2/Toggle.vue';
import { aiService } from '@/services/AIService';
import type { PromptField, JsonSchemaField, FieldCategory } from '@/types/PromptField';
import { FIELD_CATEGORIES, CATEGORY_LABELS } from '@/types/PromptField';
import { basicSetup, EditorView } from 'codemirror';
import { linter } from '@codemirror/lint';
import { EditorState } from '@codemirror/state';
import { json, jsonParseLinter } from '@codemirror/lang-json';
import { ViewUpdate } from '@codemirror/view';
import FieldItem from './FieldItem.vue';

// -----
// Props & Emits
// -----
interface Props {
  modelValue: string; // JSON string of the expected output schema
  disabled?: boolean;
  readonly?: boolean; // When true, hides editing UI and shows only the output
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  readonly: false,
});

const emit = defineEmits<Emits>();

// -----
// Constants (make available to template)
// -----
const categoryLabels = CATEGORY_LABELS;
const fieldCategories = FIELD_CATEGORIES;

// -----
// Data
// -----
const loading = ref(false);
const fieldLibrary = ref<PromptField[]>([]);
const selectedFields = ref<JsonSchemaField[]>([]);
const searchTerm = ref('');
const selectedCategory = ref<FieldCategory | ''>('');
const showExamplePayload = ref(false);

// CodeMirror editor refs
const editorRef = ref<HTMLElement | null>(null);
const editorView = ref<EditorView | null>(null);

// Sync state
const isSyncingFromEditor = ref(false); // Flag to prevent circular updates
const syncError = ref<string | null>(null);
let editorUpdateTimeout: ReturnType<typeof setTimeout> | null = null;

// Flag to track programmatic updates to CodeMirror
const isProgrammaticUpdate = ref(false);

// -----
// Computed
// -----
const filteredFields = computed(() => {
  let fields = fieldLibrary.value;

  // Filter by category
  if (selectedCategory.value) {
    fields = fields.filter((field) => field.category === selectedCategory.value);
  }

  // Filter by search term
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    fields = fields.filter(
      (field) =>
        field.displayName.toLowerCase().includes(term) ||
        field.description.toLowerCase().includes(term) ||
        field.fieldName.toLowerCase().includes(term),
    );
  }

  return fields;
});

const fieldsByCategory = computed(() => {
  const groups = new Map<string, PromptField[]>();

  filteredFields.value.forEach((field) => {
    const category = field.category || 'other';
    if (!groups.has(category)) {
      groups.set(category, []);
    }
    groups.get(category)!.push(field);
  });

  // Sort groups by category order
  const sortedEntries = Array.from(groups.entries()).sort(([a], [b]) => {
    const aOrder = Object.values(fieldCategories).indexOf(a as FieldCategory);
    const bOrder = Object.values(fieldCategories).indexOf(b as FieldCategory);
    return aOrder - bOrder;
  });

  return new Map(sortedEntries);
});

// Generate JSON schema from selected fields (array-based format)
const generateJsonSchema = (): string => {
  if (selectedFields.value.length === 0) {
    return '';
  }

  const schema: any[] = [];

  // Only process root fields (fields with no parentId)
  const rootFields = getRootFields(selectedFields.value);
  rootFields.forEach((field) => {
    schema.push(buildFieldDefinition(field));
  });

  return JSON.stringify(schema, null, 2);
};

// Computed version for template usage
const generatedJsonSchema = computed(() => generateJsonSchema());

// Build field definition for JSON schema generation (array-based format)
const buildFieldDefinition = (field: JsonSchemaField): Record<string, any> => {
  const fieldDefinition: Record<string, any> = {
    name: field.name,
    type: field.type,
    description: field.description,
    required: field.required,
  };

  // Handle nested objects - check if this field has children
  const children = getChildrenOf(field.id, selectedFields.value);

  if (field.type === 'object' && children.length > 0) {
    fieldDefinition.properties = [];
    children.forEach((child) => {
      // Skip placeholder children with names starting with '_'
      if (!child.name.startsWith('_')) {
        fieldDefinition.properties.push(buildFieldDefinition(child));
      }
    });
  }

  // Handle array items - use properties array (same as object)
  if (field.type === 'array' && children.length > 0) {
    // If array has a single child that's an object (placeholder), use its children
    if (children.length === 1 && children[0].type === 'object') {
      const itemObject = children[0];
      const itemObjectChildren = getChildrenOf(itemObject.id, selectedFields.value);

      if (itemObjectChildren.length > 0) {
        fieldDefinition.properties = [];
        itemObjectChildren.forEach((child) => {
          // Skip placeholder children with names starting with '_'
          if (!child.name.startsWith('_')) {
            fieldDefinition.properties.push(buildFieldDefinition(child));
          }
        });
      }
    } else if (children.length > 0) {
      // Multiple children directly under array
      fieldDefinition.properties = [];
      children.forEach((child) => {
        // Skip placeholder children with names starting with '_'
        if (!child.name.startsWith('_')) {
          fieldDefinition.properties.push(buildFieldDefinition(child));
        }
      });
    }
  }

  return fieldDefinition;
};

const categoryOptions = computed(() => {
  const categories = [...new Set(fieldLibrary.value.map((f) => f.category))];
  return categories.map((cat) => ({
    value: cat,
    label: categoryLabels[cat as FieldCategory] || cat,
  }));
});

// Static field type options (doesn't need to be computed)
const fieldTypeOptions = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'object', label: 'Object' },
  { value: 'array', label: 'Array' },
];

// Computed property for visible root fields (excluding placeholders)
const visibleRootFields = computed(() => {
  return getRootFields(selectedFields.value).filter((f) => !f.name.startsWith('_'));
});

// Computed properties for UISelect options
const categorySelectOptions = computed(() => {
  const options: Array<{ value: string; label: string }> = [{ value: '', label: 'All Categories' }];

  for (const category of categoryOptions.value) {
    options.push({
      value: category.value,
      label: category.label,
    });
  }

  return options;
});

// No need for fieldHierarchy - just use selectedFields directly since they have children arrays

// Always show all fields expanded (no collapse/expand logic)
// Helper functions for coverage line structure
const getCoverageLinesArrayField = (): JsonSchemaField | undefined => {
  return selectedFields.value.find((field) => field.name === 'coverageLines' && !field.parentId);
};

const getCoverageLineItemField = (): JsonSchemaField | undefined => {
  const arrayField = getCoverageLinesArrayField();
  if (!arrayField) {
    return undefined;
  }
  return selectedFields.value.find((field) => field.parentId === arrayField.id && field.name === '_coverageLineItem');
};

const ensureCoverageLineStructure = (): { arrayField: JsonSchemaField; itemObject: JsonSchemaField } => {
  let arrayField = getCoverageLinesArrayField();
  let itemObject: JsonSchemaField | undefined;

  // If no array exists, create both array and itemObject in a single state update
  if (!arrayField) {
    const arrayFieldId = generateFieldId();
    arrayField = {
      id: arrayFieldId,
      name: 'coverageLines',
      description: 'Array of coverage lines for the policy',
      type: 'array',
      required: false,
    };

    itemObject = {
      id: generateFieldId(),
      name: '_coverageLineItem',
      description: 'Coverage line object structure',
      type: 'object',
      required: false,
      parentId: arrayFieldId,
    };

    // Single batched update - critical to prevent duplication
    selectedFields.value = [...selectedFields.value, arrayField, itemObject];

    return { arrayField, itemObject };
  }

  // Array exists, check for itemObject
  itemObject = selectedFields.value.find((field) => field.parentId === arrayField.id && field.name === '_coverageLineItem');

  if (!itemObject) {
    itemObject = {
      id: generateFieldId(),
      name: '_coverageLineItem',
      description: 'Coverage line object structure',
      type: 'object',
      required: false,
      parentId: arrayField.id,
    };
    selectedFields.value = [...selectedFields.value, itemObject];
  }

  return { arrayField, itemObject };
};

const isCoverageLineFieldSelected = (libraryField: PromptField): boolean => {
  const coverageLineItem = getCoverageLineItemField();
  if (!coverageLineItem) {
    return false;
  }

  return selectedFields.value.some(
    (field) =>
      field.parentId === coverageLineItem.id &&
      (field.name === libraryField.fieldName || (field.sourceField && field.sourceField.id === libraryField.id)),
  );
};

// Check if a field is already selected
const isFieldSelected = (libraryField: PromptField): boolean => {
  if (libraryField.category === 'coverageLines') {
    return isCoverageLineFieldSelected(libraryField);
  }

  return selectedFields.value.some(
    (field) => field.name === libraryField.fieldName || (field.sourceField && field.sourceField.id === libraryField.id),
  );
};

// -----
// Lifecycle
// -----
onMounted(async () => {
  await loadFieldLibrary();
  parseExistingSchema();
});

onUnmounted(() => {
  // Clear any pending debounce timeout
  if (editorUpdateTimeout) {
    clearTimeout(editorUpdateTimeout);
    editorUpdateTimeout = null;
  }

  if (editorView.value) {
    editorView.value.destroy();
    editorView.value = null;
  }
});

// Watch for external changes to modelValue
watch(
  () => props.modelValue,
  () => {
    parseExistingSchema();
  },
);

// -----
// CodeMirror Helper Functions (defined early for use in watchers)
// -----

// Helper to get current editor content based on mode
const getCurrentEditorContent = (): string => {
  return showExamplePayload.value ? generateExamplePayload() : generateJsonSchema();
};

// Helper to update editor content
const updateEditorContent = (content: string) => {
  if (editorView.value) {
    const currentDoc = editorView.value.state.doc.toString();
    if (currentDoc !== content) {
      // Set flag to indicate this is a programmatic update
      isProgrammaticUpdate.value = true;

      editorView.value.dispatch({
        changes: { from: 0, to: editorView.value.state.doc.length, insert: content },
      });

      // Reset flag after the update is processed
      setTimeout(() => {
        isProgrammaticUpdate.value = false;
      }, 0);
    }
  }
};

// Watch for changes to selectedFields - emit updates and update editor
watch(
  selectedFields,
  () => {
    if (!isSyncingFromEditor.value) {
      const newSchema = generateJsonSchema();
      emit('update:modelValue', newSchema);

      // Update editor content if it exists
      if (editorView.value) {
        const currentContent = getCurrentEditorContent();
        updateEditorContent(currentContent);
      }
    }
  },
  { deep: true },
);

// -----
// Hierarchy Utility Functions
// -----

/**
 * Get all root fields (fields with no parentId)
 */
const getRootFields = (fields: JsonSchemaField[]): JsonSchemaField[] => {
  return fields.filter((field) => !field.parentId);
};

/**
 * Get all children of a specific parent field
 */
const getChildrenOf = (parentId: string, fields: JsonSchemaField[]): JsonSchemaField[] => {
  return fields.filter((field) => field.parentId === parentId);
};

/**
 * Get the full path of a field (e.g., "parent.child.grandchild")
 * Skips placeholder fields (names starting with '_') when building the path
 */
const getFieldPath = (fieldId: string, fields: JsonSchemaField[] = selectedFields.value): string => {
  const field = fields.find((f) => f.id === fieldId);
  if (!field) return '';

  // Skip placeholder fields - if this is a placeholder, get its parent's path instead
  if (field.name.startsWith('_')) {
    if (!field.parentId) return '';
    return getFieldPath(field.parentId, fields);
  }

  if (!field.parentId) return field.name;

  const parent = fields.find((f) => f.id === field.parentId);
  if (!parent) return field.name;

  // If parent is a placeholder, skip it and go to its parent
  if (parent.name.startsWith('_')) {
    if (!parent.parentId) return field.name;
    const grandParent = fields.find((f) => f.id === parent.parentId);
    if (!grandParent) return field.name;
    return `${getFieldPath(grandParent.id, fields)}.${field.name}`;
  }

  return `${getFieldPath(parent.id, fields)}.${field.name}`;
};

/**
 * Remove a field and all its descendants
 */
const removeFieldAndDescendants = (fieldId: string, fields: JsonSchemaField[]): JsonSchemaField[] => {
  // Get all descendants recursively
  const getDescendants = (parentId: string): JsonSchemaField[] => {
    const children = getChildrenOf(parentId, fields);
    const descendants: JsonSchemaField[] = [...children];
    children.forEach((child) => {
      descendants.push(...getDescendants(child.id));
    });
    return descendants;
  };

  const descendants = getDescendants(fieldId);
  const idsToRemove = new Set([fieldId, ...descendants.map((d) => d.id)]);
  return fields.filter((field) => !idsToRemove.has(field.id));
};

// -----
// Methods
// -----
const loadFieldLibrary = async () => {
  loading.value = true;
  try {
    fieldLibrary.value = await aiService.getAllPromptFields();
  } catch (error) {
    console.error('Failed to load field library:', error);
  } finally {
    loading.value = false;
  }
};

const parseExistingSchema = () => {
  if (!props.modelValue) {
    selectedFields.value = [];
    return;
  }

  try {
    const schema = JSON.parse(props.modelValue) as Record<string, any>;
    selectedFields.value = parseSchemaToFields(schema);
  } catch (error) {
    console.error('Failed to parse existing schema:', error);
    selectedFields.value = [];
  }
};

// Generate a unique field name by checking for existing names
const generateUniqueFieldName = (baseName: string, parentId?: string): string => {
  const siblings = parentId ? getChildrenOf(parentId, selectedFields.value) : getRootFields(selectedFields.value);

  const existingNames = siblings.map((f) => f.name);
  let counter = 1;
  let uniqueName = baseName;

  while (existingNames.includes(uniqueName)) {
    uniqueName = `${baseName}${counter}`;
    counter++;
  }

  return uniqueName;
};

const generateFieldId = () => {
  return `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// -----
// Bidirectional Sync Methods
// -----

/**
 * Handle editor changes with debouncing
 */
const handleEditorUpdate = (update: ViewUpdate) => {
  // Skip if this is a programmatic update
  if (isProgrammaticUpdate.value) {
    return;
  }

  if (update.docChanged && !isSyncingFromEditor.value && !showExamplePayload.value) {
    // Clear any existing timeout
    if (editorUpdateTimeout) {
      clearTimeout(editorUpdateTimeout);
    }

    // Debounce the sync to avoid excessive parsing
    editorUpdateTimeout = setTimeout(() => {
      syncFromEditor();
    }, 500); // 500ms debounce
  }
};

/**
 * Sync changes from CodeMirror editor to selectedFields
 */
const syncFromEditor = () => {
  if (!editorView.value) return;

  const jsonString = editorView.value.state.doc.toString().trim();

  // Clear previous errors
  syncError.value = null;

  // Handle empty JSON - clear all fields
  if (!jsonString) {
    isSyncingFromEditor.value = true;
    selectedFields.value = [];
    isSyncingFromEditor.value = false;
    return;
  }

  try {
    // Parse the JSON
    const schema = JSON.parse(jsonString) as Record<string, any>;

    // Prevent circular updates
    isSyncingFromEditor.value = true;

    // Update selectedFields with smart merge
    const newFields = parseSchemaToFields(schema);
    selectedFields.value = newFields;
  } catch (error) {
    console.error('[syncFromEditor] Failed to parse JSON:', error);
    syncError.value = error instanceof Error ? error.message : 'Invalid JSON';
  } finally {
    isSyncingFromEditor.value = false;
  }
};

/**
 * Parse schema JSON into flat JsonSchemaField array (array-based format)
 * Preserves existing IDs where possible for bidirectional sync
 */
const parseSchemaToFields = (schema: any): JsonSchemaField[] => {
  const fields: JsonSchemaField[] = [];

  // Handle empty schema or non-array
  if (!schema || !Array.isArray(schema) || schema.length === 0) {
    return fields;
  }

  // Track which fields we've processed to avoid duplicates
  const processedFieldIds = new Set<string>();

  // Parse each field in the array
  schema.forEach((fieldDef) => {
    if (!fieldDef.name) return; // Skip invalid entries

    // Try to find existing field with same name to preserve ID and metadata
    const existingField = selectedFields.value.find((f) => f.name === fieldDef.name && !f.parentId);

    const field: JsonSchemaField = {
      id: existingField?.id || generateFieldId(),
      name: fieldDef.name,
      description: fieldDef.description || '',
      type: fieldDef.type || 'string',
      required: fieldDef.required === true,
      parentId: undefined, // Root field
      sourceField: existingField?.sourceField,
    };

    processedFieldIds.add(field.id);

    // Handle nested properties (array of fields)
    if (fieldDef.properties && Array.isArray(fieldDef.properties) && fieldDef.properties.length > 0) {
      if (fieldDef.type === 'object') {
        const childFields = parseChildFieldsFromArray(fieldDef.properties, field.id, processedFieldIds);
        fields.push(...childFields);
      } else if (fieldDef.type === 'array') {
        // For arrays, create placeholder object and add children to it
        const placeholderName = field.name === 'coverageLines' ? '_coverageLineItem' : '_item';
        const existingPlaceholder = selectedFields.value.find((f) => f.parentId === field.id && f.name === placeholderName);

        const placeholderChild: JsonSchemaField = {
          id: existingPlaceholder?.id || generateFieldId(),
          name: placeholderName,
          description: existingPlaceholder?.description || 'Array item object structure',
          type: 'object',
          required: false,
          parentId: field.id,
          sourceField: existingPlaceholder?.sourceField,
        };

        processedFieldIds.add(placeholderChild.id);
        fields.push(placeholderChild);

        const childFields = parseChildFieldsFromArray(fieldDef.properties, placeholderChild.id, processedFieldIds);
        fields.push(...childFields);
      }
    }

    fields.push(field);
  });

  return fields;
};

/**
 * Parse child fields recursively from array format into flat structure
 */
const parseChildFieldsFromArray = (properties: any[], parentId: string, processedFieldIds: Set<string>): JsonSchemaField[] => {
  const children: JsonSchemaField[] = [];

  // Handle empty properties
  if (!properties || properties.length === 0) {
    return children;
  }

  properties.forEach((childProp) => {
    if (!childProp.name) return; // Skip invalid entries

    // Find existing child with this name and parent
    const existingChild = selectedFields.value.find((c) => c.name === childProp.name && c.parentId === parentId);

    const child: JsonSchemaField = {
      id: existingChild?.id || generateFieldId(),
      name: childProp.name,
      description: childProp.description || '',
      type: childProp.type || 'string',
      required: childProp.required === true,
      parentId: parentId,
      sourceField: existingChild?.sourceField,
    };

    processedFieldIds.add(child.id);

    // Recursively handle nested properties
    if (childProp.properties && Array.isArray(childProp.properties) && childProp.properties.length > 0) {
      if (childProp.type === 'object') {
        const grandChildren = parseChildFieldsFromArray(childProp.properties, child.id, processedFieldIds);
        children.push(...grandChildren);
      } else if (childProp.type === 'array') {
        // Create placeholder for array items
        const placeholderChild: JsonSchemaField = {
          id: generateFieldId(),
          name: '_item',
          description: 'Array item object structure',
          type: 'object',
          required: false,
          parentId: child.id,
        };
        processedFieldIds.add(placeholderChild.id);
        children.push(placeholderChild);

        const grandChildren = parseChildFieldsFromArray(childProp.properties, placeholderChild.id, processedFieldIds);
        children.push(...grandChildren);
      }
    }

    children.push(child);
  });

  return children;
};

const addFieldFromLibrary = (libraryField: PromptField) => {
  if (libraryField.category === 'coverageLines') {
    addCoverageLineField(libraryField);
    return;
  }

  // Check if field already exists
  const existingField = selectedFields.value.find(
    (field) => field.name === libraryField.fieldName || (field.sourceField && field.sourceField.id === libraryField.id),
  );

  if (existingField) {
    // Could show a toast notification here
    console.warn(`Field "${libraryField.displayName}" is already added to the schema`);
    return;
  }

  const fieldId = generateFieldId();
  const jsonType = mapDataTypeToJsonType(libraryField.dataType);

  const newField: JsonSchemaField = {
    id: fieldId,
    name: libraryField.fieldName,
    description: libraryField.description,
    type: jsonType,
    required: false,
    sourceField: libraryField,
  };

  selectedFields.value = [...selectedFields.value, newField];
};

const mapDataTypeToJsonType = (dataType: string): 'string' | 'number' | 'boolean' => {
  switch (dataType) {
    case 'number':
    case 'decimal':
      return 'number';
    case 'boolean':
      return 'boolean';
    default:
      return 'string';
  }
};

const addCustomField = () => {
  const uniqueName = generateUniqueFieldName('newField');

  const newField: JsonSchemaField = {
    id: generateFieldId(),
    name: uniqueName,
    description: 'Custom field description',
    type: 'string',
    required: false,
  };

  selectedFields.value = [...selectedFields.value, newField];
};

const addAllVisibleFields = () => {
  const availableFields = filteredFields.value.filter((field) => !isFieldSelected(field));

  if (availableFields.length === 0) {
    console.warn('No available fields to add');
    return;
  }

  const newFields: JsonSchemaField[] = availableFields.map((libraryField) => {
    const fieldId = generateFieldId();
    const jsonType = mapDataTypeToJsonType(libraryField.dataType);

    return {
      id: fieldId,
      name: libraryField.fieldName,
      description: libraryField.description,
      type: jsonType,
      required: false,
      sourceField: libraryField,
    };
  });

  selectedFields.value = [...selectedFields.value, ...newFields];

  console.log(`Added ${newFields.length} visible fields to schema`);
};

const addCoverageLineField = (libraryField: PromptField) => {
  const { itemObject } = ensureCoverageLineStructure();

  const existingField = selectedFields.value.find(
    (field) =>
      field.parentId === itemObject.id && (field.name === libraryField.fieldName || (field.sourceField && field.sourceField.id === libraryField.id)),
  );

  if (existingField) {
    console.warn(`Coverage line field "${libraryField.displayName}" is already added to the schema`);
    return;
  }

  const jsonType = mapDataTypeToJsonType(libraryField.dataType);

  const newField: JsonSchemaField = {
    id: generateFieldId(),
    name: libraryField.fieldName,
    description: libraryField.description,
    type: jsonType,
    required: false,
    parentId: itemObject.id,
    sourceField: libraryField,
  };

  selectedFields.value = [...selectedFields.value, newField];
};

// Add Coverage Lines as an array of objects
const addCoverageLineArray = () => {
  const coverageLineFields = fieldLibrary.value.filter((f) => f.category === 'coverageLines' && f.isActive);

  if (coverageLineFields.length === 0) {
    console.warn('No coverage line fields found in library');
    return;
  }

  const { itemObject } = ensureCoverageLineStructure();

  // Check for existing fields by both name and sourceField.id to avoid duplicates
  const existingFields = selectedFields.value.filter((field) => field.parentId === itemObject.id);
  const existingFieldNames = new Set(existingFields.map((field) => field.name));
  const existingSourceFieldIds = new Set(existingFields.filter((f) => f.sourceField).map((f) => f.sourceField!.id));

  const newFields: JsonSchemaField[] = coverageLineFields
    .filter((libraryField) => !existingFieldNames.has(libraryField.fieldName) && !existingSourceFieldIds.has(libraryField.id))
    .map((libraryField) => {
      const jsonType = mapDataTypeToJsonType(libraryField.dataType);
      return {
        id: generateFieldId(),
        name: libraryField.fieldName,
        description: libraryField.description,
        type: jsonType,
        required: false,
        parentId: itemObject.id,
        sourceField: libraryField,
      };
    });

  if (newFields.length === 0) {
    console.warn('All coverage line fields are already added to the schema');
    return;
  }

  selectedFields.value = [...selectedFields.value, ...newFields];
};

const copySchema = async () => {
  try {
    const contentToCopy = showExamplePayload.value ? generatedJsonSchema.value : examplePayload.value;
    await navigator.clipboard.writeText(contentToCopy);
    // Could add a toast notification here
  } catch (error) {
    console.error('Failed to copy schema:', error);
  }
};

const handleFormatOutput = () => {
  const output = editorView.value?.state.doc.toString() ?? '';
  let result = output;
  try {
    result = JSON.stringify(JSON.parse(output), null, 2);
  } catch (e) {
    console.error('Failed to format JSON:', e);
  }
  editorView.value?.dispatch({
    changes: { from: 0, to: editorView.value.state.doc.length, insert: result },
  });
};

const clearAllFields = () => {
  selectedFields.value = [];
};

// Nested field management methods - working with flat structure
const addChildField = (parentId: string) => {
  const uniqueName = generateUniqueFieldName('newChildField', parentId);

  const newChild: JsonSchemaField = {
    id: generateFieldId(),
    name: uniqueName,
    description: 'Child field description',
    type: 'string',
    required: false,
    parentId: parentId, // Set parent reference
  };

  // Add the new child field
  selectedFields.value = [...selectedFields.value, newChild];

  // Convert parent to object type if it isn't already
  selectedFields.value = selectedFields.value.map((field) => {
    if (field.id === parentId && field.type !== 'object') {
      return { ...field, type: 'object' as const };
    }
    return field;
  });
};

const removeFieldWithChildren = (fieldId: string) => {
  // Remove the field and all its descendants using utility function
  selectedFields.value = removeFieldAndDescendants(fieldId, selectedFields.value);
};

// Promote field - move child to root level
const promoteField = (childId: string) => {
  // Find the child field to promote
  const childToPromote = selectedFields.value.find((f) => f.id === childId);
  if (!childToPromote) {
    console.error('[promoteField] Child field not found:', childId);
    return;
  }

  // Move the child to root level (remove parentId)
  selectedFields.value = selectedFields.value.map((field) => {
    if (field.id === childId) {
      return { ...field, parentId: null };
    }
    return field;
  });
};

const isFieldNameDuplicatedInLevel = (fieldName: string, siblings: JsonSchemaField[]): boolean => {
  const names = siblings.map((field) => field.name.toLowerCase());
  const seen = new Set<string>();

  for (const name of names) {
    if (seen.has(name)) {
      return true;
    }
    seen.add(name);
  }

  return false;
};

const updateField = (fieldId: string, updates: Partial<JsonSchemaField>) => {
  // Simple update - find field by ID and update it
  selectedFields.value = selectedFields.value.map((field) => {
    if (field.id === fieldId) {
      return { ...field, ...updates };
    }
    return field;
  });
};

// Generate example payload with mock data (array-based format with value fields)
const generateExamplePayload = (): string => {
  if (selectedFields.value.length === 0) {
    return '';
  }

  const payload: any[] = [];

  // Only process root fields (fields with no parentId)
  const rootFields = getRootFields(selectedFields.value);
  rootFields.forEach((field) => {
    // Skip placeholder fields with names starting with '_'
    if (!field.name.startsWith('_')) {
      payload.push(buildExampleFieldDefinition(field));
    }
  });

  return JSON.stringify(payload, null, 2);
};

// Computed version for template usage
const examplePayload = computed(() => generateExamplePayload());

// Build example field definition with mock values (array-based format)
const buildExampleFieldDefinition = (field: JsonSchemaField): Record<string, any> => {
  const fieldDef: Record<string, any> = {
    name: field.name,
    type: field.type,
    description: field.description,
    required: field.required,
  };

  const children = getChildrenOf(field.id, selectedFields.value);

  if (field.type === 'object' && children.length > 0) {
    fieldDef.properties = [];
    children.forEach((child) => {
      if (!child.name.startsWith('_')) {
        fieldDef.properties.push(buildExampleFieldDefinition(child));
      }
    });
  } else if (field.type === 'array' && children.length > 0) {
    fieldDef.properties = [];
    // Handle array items - if single placeholder object, use its children
    if (children.length === 1 && children[0].type === 'object') {
      const itemChildren = getChildrenOf(children[0].id, selectedFields.value);
      itemChildren.forEach((child) => {
        if (!child.name.startsWith('_')) {
          fieldDef.properties.push(buildExampleFieldDefinition(child));
        }
      });
    } else {
      children.forEach((child) => {
        if (!child.name.startsWith('_')) {
          fieldDef.properties.push(buildExampleFieldDefinition(child));
        }
      });
    }
  } else {
    // Leaf field - add example value
    fieldDef.value = generateMockValue(field);
  }

  return fieldDef;
};

// Generate mock value for a leaf field
const generateMockValue = (field: JsonSchemaField): any => {
  switch (field.type) {
    case 'string':
      return field.name.toLowerCase().includes('name')
        ? 'Sample Account Name'
        : field.name.toLowerCase().includes('description')
          ? 'Sample description text'
          : field.name.toLowerCase().includes('email')
            ? 'user@example.com'
            : field.name.toLowerCase().includes('url')
              ? 'https://example.com'
              : field.name.toLowerCase().includes('id')
                ? 'sample-id-123'
                : field.name.toLowerCase().includes('code')
                  ? 'ABC123'
                  : 'Sample text';
    case 'number':
      return field.name.toLowerCase().includes('limit')
        ? 1000000
        : field.name.toLowerCase().includes('amount')
          ? 50000
          : field.name.toLowerCase().includes('count')
            ? 25
            : field.name.toLowerCase().includes('rate')
              ? 0.05
              : field.name.toLowerCase().includes('percentage')
                ? 85
                : 1234;
    case 'boolean':
      return field.name.toLowerCase().includes('active')
        ? true
        : field.name.toLowerCase().includes('enabled')
          ? true
          : field.name.toLowerCase().includes('disabled')
            ? false
            : field.name.toLowerCase().includes('required')
              ? true
              : false;
    default:
      return 'Sample data';
  }
};

// -----
// Helper function to get children, skipping placeholder fields
// If a child is a placeholder, return its children instead
const getChildrenOfFiltered = (parentId: string): JsonSchemaField[] => {
  if (!parentId) {
    return [];
  }

  const directChildren = getChildrenOf(parentId, selectedFields.value);
  const result: JsonSchemaField[] = [];
  const seenIds = new Set<string>(); // Track IDs to prevent duplicates
  const seenNames = new Set<string>(); // Also track by name as additional guard

  directChildren.forEach((child) => {
    if (child.name.startsWith('_')) {
      // If it's a placeholder, include its children instead (unwrap)
      const placeholderChildren = getChildrenOf(child.id, selectedFields.value);
      placeholderChildren.forEach((grandChild) => {
        const nameKey = `${grandChild.name}_${grandChild.parentId}`;
        if (!seenIds.has(grandChild.id) && !seenNames.has(nameKey)) {
          seenIds.add(grandChild.id);
          seenNames.add(nameKey);
          result.push(grandChild);
        }
      });
    } else {
      const nameKey = `${child.name}_${child.parentId}`;
      if (!seenIds.has(child.id) && !seenNames.has(nameKey)) {
        seenIds.add(child.id);
        seenNames.add(nameKey);
        result.push(child);
      }
    }
  });

  return result;
};

// Helper to get actual siblings (for duplicate checking) - uses real parentId, not filtered
const getActualSiblings = (fieldId: string): JsonSchemaField[] => {
  const field = selectedFields.value.find((f) => f.id === fieldId);
  if (!field) {
    return [];
  }

  if (!field.parentId) {
    // Root level - get all root fields (excluding placeholders)
    return getRootFields(selectedFields.value).filter((f) => !f.name.startsWith('_'));
  }

  // Get all fields with the same parentId (excluding the field itself)
  return selectedFields.value.filter((f) => f.parentId === field.parentId && f.id !== fieldId);
};

// Provide context for recursive FieldItem components (must be called during setup, not onMounted)
// -----
provide('fieldBuilderContext', {
  fieldTypeOptions,
  getFieldPath: (fieldId: string) => getFieldPath(fieldId, selectedFields.value),
  isFieldNameDuplicatedInLevel,
  getChildrenOf: (parentId: string) => getChildrenOf(parentId, selectedFields.value),
  getChildrenOfFiltered: (parentId: string) => getChildrenOfFiltered(parentId),
  getActualSiblings: (fieldId: string) => getActualSiblings(fieldId),
});

// -----
// CodeMirror Watchers
// -----

// Initialize CodeMirror when editorRef is available
watch(editorRef, (ef) => {
  if (ef !== null) {
    const initialDoc = getCurrentEditorContent();

    // Create update listener for bidirectional sync
    const updateListener = EditorView.updateListener.of((update: ViewUpdate) => {
      handleEditorUpdate(update);
    });

    const startState = EditorState.create({
      doc: initialDoc,
      extensions: [
        basicSetup,
        json(),
        linter(jsonParseLinter()),
        updateListener,
        EditorView.editable.of(!props.disabled), // Respect the disabled prop
      ],
    });

    editorView.value = new EditorView({
      state: startState,
      parent: ef,
    });
  }
});

// Watch for toggle changes to update editor content
watch(showExamplePayload, () => {
  if (!isSyncingFromEditor.value) {
    updateEditorContent(getCurrentEditorContent());
    syncError.value = null;
  }
});
</script>

<template>
  <div class="tw:flex tw:min-h-0 tw:flex-1 tw:gap-4">
    <!-- Left Column: Field Library and Selected Fields (hidden in readonly mode) -->
    <div
      v-if="!props.readonly"
      class="tw:flex tw:min-h-0 tw:w-1/2 tw:flex-col tw:space-y-4">
      <p
        v-if="props.disabled"
        class="tw:text-xs tw:text-slate-500 tw:italic">
        Editing is only allowed for draft prompts
      </p>

      <!-- Field Library Panel -->
      <Card class="tw:shrink-0 tw:border-blue-200">
        <CardHeader class="tw:pb-3">
          <div class="tw:flex tw:items-center tw:justify-between">
            <CardTitle class="tw:text-sm tw:text-blue-700">Field Library</CardTitle>
            <Button
              size="sm"
              variant="outline"
              class="tw:gap-1 tw:border-blue-200 tw:text-blue-600 tw:hover:bg-blue-50"
              :disabled="props.disabled || loading || filteredFields.filter((f) => !isFieldSelected(f)).length === 0"
              @click="addAllVisibleFields">
              <Plus class="tw:h-3 tw:w-3" />
              Add All Visible ({{ filteredFields.filter((f) => !isFieldSelected(f)).length }})
            </Button>
          </div>

          <!-- Search and Filter -->
          <div class="tw:mt-2 tw:flex tw:gap-2">
            <div class="tw:relative tw:min-w-0 tw:flex-2">
              <Input
                v-model="searchTerm"
                placeholder="Search fields..."
                size="sm" />
              <button
                v-if="searchTerm"
                @click="searchTerm = ''"
                class="tw:absolute tw:top-1/2 tw:right-2 tw:-translate-y-1/2 tw:text-slate-400 tw:hover:text-slate-600"
                title="Clear search">
                <X class="tw:h-4 tw:w-4" />
              </button>
            </div>

            <div class="tw:flex-1 tw:shrink-0">
              <UISelect
                v-model="selectedCategory"
                :options="categorySelectOptions"
                placeholder="All Categories" />
            </div>
          </div>
        </CardHeader>

        <CardContent class="tw:max-h-64 tw:overflow-y-auto">
          <div
            v-if="loading"
            class="tw:py-4 tw:text-center tw:text-sm tw:text-slate-500">
            Loading fields...
          </div>

          <div
            v-else-if="filteredFields.length === 0"
            class="tw:py-4 tw:text-center tw:text-sm tw:text-slate-500">
            No fields found
          </div>

          <div
            v-else
            class="tw:space-y-3">
            <div
              v-for="[category, fields] in fieldsByCategory"
              :key="category"
              class="tw:space-y-2">
              <div class="tw:flex tw:items-center tw:gap-2">
                <h5 class="tw:text-xs tw:font-semibold tw:tracking-wide tw:text-slate-600 tw:uppercase">
                  {{ categoryLabels[category as FieldCategory] || category }}
                </h5>
              </div>

              <div class="tw:space-y-1">
                <div
                  v-for="field in fields"
                  :key="field.id"
                  :class="[
                    'tw:flex tw:items-start tw:justify-between tw:rounded tw:border tw:p-2',
                    isFieldSelected(field)
                      ? 'tw:cursor-not-allowed tw:border-green-200 tw:bg-green-50 tw:opacity-75'
                      : 'tw:cursor-pointer tw:border-slate-200 tw:hover:bg-blue-50',
                  ]"
                  @click="!isFieldSelected(field) && addFieldFromLibrary(field)">
                  <div class="tw:flex-1">
                    <div class="tw:flex tw:items-center tw:gap-2">
                      <div class="tw:text-sm tw:font-medium tw:text-slate-900">{{ field.displayName }}</div>
                      <span
                        v-if="isFieldSelected(field)"
                        class="tw:rounded tw:bg-green-100 tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium tw:text-green-700">
                        Added
                      </span>
                    </div>
                    <div class="tw:text-xs tw:text-slate-500">{{ field.description }}</div>
                    <div class="tw:text-xs tw:text-blue-600">{{ field.dataType }}</div>
                  </div>
                  <div
                    v-if="isFieldSelected(field)"
                    class="tw:mt-0.5">
                    <svg
                      class="tw:h-4 tw:w-4 tw:text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <Plus
                    v-else
                    class="tw:mt-0.5 tw:h-4 tw:w-4 tw:text-blue-600" />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- Control Buttons -->
      <div class="tw:flex tw:shrink-0 tw:flex-wrap tw:items-center tw:gap-2">
        <Button
          size="sm"
          variant="outline"
          class="tw:gap-1 tw:border-green-200 tw:text-green-600 tw:hover:bg-green-50"
          :disabled="props.disabled || fieldLibrary.filter((f) => f.category === 'coverageLines' && f.isActive).length === 0"
          @click="addCoverageLineArray">
          <Plus class="tw:h-3 tw:w-3" />
          Add Coverage Lines
        </Button>

        <Button
          size="sm"
          variant="outline"
          class="tw:gap-1"
          :disabled="props.disabled"
          @click="addCustomField">
          <Plus class="tw:h-3 tw:w-3" />
          Custom Field
        </Button>

        <Button
          size="sm"
          variant="outline"
          class="tw:gap-1 tw:border-red-200 tw:text-red-600 tw:hover:bg-red-50"
          :disabled="props.disabled || selectedFields.length === 0"
          @click="clearAllFields">
          <Trash2 class="tw:h-3 tw:w-3" />
          Clear All
        </Button>
      </div>

      <!-- Selected Fields -->
      <div
        v-if="selectedFields.length > 0"
        class="tw:min-h-0 tw:flex-1 tw:space-y-2 tw:overflow-auto">
        <h5 class="tw:text-sm tw:font-medium tw:text-slate-700">
          Selected Fields ({{ selectedFields.filter((f) => !f.name.startsWith('_')).length }})
        </h5>

        <div class="tw:space-y-1">
          <FieldItem
            v-for="field in visibleRootFields"
            :key="field.id"
            :field="field"
            :depth="0"
            :disabled="props.disabled"
            @add-child="addChildField"
            @remove-field="removeFieldWithChildren"
            @update-field="updateField"
            @promote-field="promoteField"
            @toggle-expansion="() => {}" />
        </div>
      </div>
    </div>

    <!-- Right Column: JSON Preview (full width in readonly mode) -->
    <div
      :class="props.readonly ? 'tw:w-full' : 'tw:w-1/2'"
      class="tw:flex tw:min-h-0 tw:flex-col">
      <!-- JSON Preview -->
      <div
        v-if="generatedJsonSchema"
        class="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:space-y-2">
        <div class="tw:flex tw:shrink-0 tw:items-center tw:justify-between">
          <div class="tw:flex tw:items-center tw:gap-2">
            <h5 class="tw:text-sm tw:font-medium tw:text-slate-700">
              {{ showExamplePayload ? 'Example Output' : 'Prompt Schema' }}
            </h5>
            <span
              v-if="!showExamplePayload && !props.disabled"
              class="tw:rounded tw:bg-blue-100 tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium tw:text-blue-700"
              title="Changes to the JSON will update the field builder">
              Editable
            </span>
          </div>
          <div class="tw:flex tw:items-center tw:gap-2">
            <Toggle
              id="example-payload-toggle"
              label="Show Example Output"
              v-model="showExamplePayload" />
            <Button
              size="sm"
              variant="outline"
              class="tw:gap-1"
              @click="handleFormatOutput">
              <Braces class="tw:h-3 tw:w-3" />
              Format
            </Button>
            <Button
              size="sm"
              variant="outline"
              class="tw:gap-1"
              @click="copySchema">
              <Copy class="tw:h-3 tw:w-3" />
              Copy
            </Button>
          </div>
        </div>

        <!-- Sync Error Alert -->
        <div
          v-if="syncError && !showExamplePayload"
          class="tw:flex tw:items-start tw:gap-2 tw:rounded tw:border tw:border-red-200 tw:bg-red-50 tw:p-2 tw:text-sm">
          <svg
            class="tw:h-5 tw:w-5 tw:shrink-0 tw:text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <div class="tw:flex-1">
            <p class="tw:font-medium tw:text-red-800">JSON Sync Error</p>
            <p class="tw:text-red-700">{{ syncError }}</p>
            <p class="tw:mt-1 tw:text-xs tw:text-red-600">Fix the JSON syntax to sync changes back to the field builder.</p>
          </div>
          <button
            @click="syncError = null"
            class="tw:shrink-0 tw:text-red-400 tw:hover:text-red-600"
            title="Dismiss">
            <X class="tw:h-4 tw:w-4" />
          </button>
        </div>

        <div
          ref="editorRef"
          class="tw:min-h-0 tw:flex-1 tw:overflow-auto tw:rounded tw:border tw:bg-slate-50"></div>
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="tw:flex tw:flex-1 tw:items-center tw:justify-center">
        <div class="tw:text-center tw:text-sm tw:text-slate-500">
          <div class="tw:mb-2">{{ props.readonly ? 'No expected output defined' : 'No fields selected' }}</div>
          <div v-if="!props.readonly">Add fields from the library or create custom fields to define your expected JSON output</div>
        </div>
      </div>
    </div>
  </div>
</template>
